import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { seoHead } from "@/lib/seo";
import {
  PRODUCTS,
  SHIPPING_NOTE,
  getProduct,
  productJsonLd,
  shopBreadcrumbsJsonLd,
  defaultSelection,
  describeSelection,
  isSoldOut,
  optionLabel,
  optionStock,
  stockForSelection,
  stockLabel,
  type Product,
  type VariantSelection,
} from "@/lib/shop-products";
import { reportShopRouteError, useShopSentry } from "@/lib/shop-telemetry";
import { useCart } from "@/lib/cart";

export const Route = createFileRoute("/shop_/$productId")({
  loader: ({ params }) => {
    const product = getProduct(params.productId);
    if (!product) throw notFound();
    return { product };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return seoHead({
        path: "/shop",
        title: "Product not found — Coffman Cove Lodging Shop",
        description:
          "This lodge shop item isn't available. Browse the rest of the Coffman Cove Lodging shop for apparel, gear, and Alaskan keepsakes.",
        extraMeta: [{ name: "robots", content: "noindex, follow" }],
      });
    }
    const { product } = loaderData;
    // Trim to <160 chars for the SERP snippet, then append the price + shipping
    // hook so the meta description sells the click.
    const trimmedBlurb =
      product.blurb.length > 130
        ? `${product.blurb.slice(0, 127).trimEnd()}…`
        : product.blurb;
    const description = `${trimmedBlurb} $${product.price} — ships to your door in the lower 48.`;
    return seoHead({
      path: `/shop/${product.id}`,
      title: `${product.name} — $${product.price} | Coffman Cove Lodging Shop`,
      description,
      image: product.image,
      imageAlt: `${product.name} — Coffman Cove Lodging`,
      type: "product",
      extraMeta: [
        { property: "product:price:amount", content: product.price.toFixed(2) },
        { property: "product:price:currency", content: "USD" },
        {
          property: "product:availability",
          content: isSoldOut(product) ? "out of stock" : "in stock",
        },
        { property: "product:brand", content: "Coffman Cove Lodging" },
        { property: "product:category", content: product.category },
        { property: "product:condition", content: "new" },
        { property: "product:retailer_item_id", content: product.id },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(productJsonLd(product)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(shopBreadcrumbsJsonLd(product)),
        },
      ],
    });
  },
  notFoundComponent: ProductNotFound,
  errorComponent: ProductError,
  component: ProductPage,
});

function ProductError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  const { productId } = Route.useParams();
  useEffect(() => {
    reportShopRouteError(error, { view: "product", productId });
  }, [error, productId]);
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-20 text-center">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--color-ember)]">
        Lodge Shop
      </p>
      <h1 className="mt-3 font-display text-3xl sm:text-4xl">
        This product page hit a snag.
      </h1>
      <p className="mt-3 text-muted-foreground">
        Usually a momentary hiccup — retry, or head back to the shop and try again.
      </p>
      {import.meta.env.DEV && error?.message && (
        <pre className="mt-6 mx-auto max-w-xl overflow-auto rounded-md border border-border bg-card p-3 text-left text-xs text-muted-foreground">
          {error.message}
        </pre>
      )}
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="rounded-md bg-[var(--color-ember)] px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
        >
          Retry
        </button>
        <Link
          to="/shop"
          className="rounded-md border border-border px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
        >
          Back to the shop
        </Link>
      </div>
    </div>
  );
}

function ProductNotFound() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Product not found</h1>
      <p className="mt-3 text-muted-foreground">
        That item may have sold out or been renamed.
      </p>
      <Link
        to="/shop"
        className="mt-6 inline-block rounded-md bg-[var(--color-ember)] px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)]"
      >
        Back to the shop
      </Link>
    </div>
  );
}

function ProductPage() {
  const { product } = Route.useLoaderData() as { product: Product };
  useShopSentry("product", { productId: product.id, productName: product.name });
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [selection, setSelection] = useState<VariantSelection>(() =>
    defaultSelection(product),
  );

  const stock = stockForSelection(product, selection);
  const soldOut = isSoldOut(product) || stock === "sold_out";
  const selectionLabel = describeSelection(product, selection);

  const related = PRODUCTS.filter((p) => p.id !== product.id).slice(0, 3);

  return (
    <div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-8">
        <nav className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          <Link to="/shop" className="hover:text-[var(--color-ember)]">
            Shop
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{product.category}</span>
        </nav>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-14 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-[var(--color-navy)] to-[var(--color-navy-deep)] flex items-center justify-center">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="font-display text-8xl text-[var(--color-ember)] opacity-30">
              {product.name.charAt(0)}
            </span>
          )}
          {product.badge && (
            <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[var(--color-ember)] text-[var(--color-ember-foreground)]">
              {product.badge}
            </span>
          )}
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--color-ember)]">
            {product.category}
          </p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl text-balance">
            {product.name}
          </h1>
          <p className="mt-4 text-2xl font-semibold">${product.price}</p>
          <p
            className={`mt-2 text-xs uppercase tracking-[0.2em] ${
              soldOut ? "text-muted-foreground" : "text-[var(--color-ember)]"
            }`}
            aria-live="polite"
          >
            {stockLabel(soldOut ? "sold_out" : stock)}
          </p>

          <div className="mt-6 space-y-4 text-base text-muted-foreground">
            {product.description.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <ul className="mt-6 space-y-2 text-sm">
            {product.details.map((d) => (
              <li key={d} className="flex gap-2">
                <span className="text-[var(--color-ember)]">•</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>

          {product.variants?.length ? (
            <div className="mt-8 space-y-5">
              {product.variants.map((group) => (
                <fieldset key={group.id}>
                  <legend className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    {group.label}
                  </legend>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {group.options.map((option) => {
                      const optSoldOut =
                        optionStock(product, option) === "sold_out";
                      const active = selection[group.id] === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={optSoldOut}
                          aria-pressed={active}
                          onClick={() =>
                            setSelection((s) => ({
                              ...s,
                              [group.id]: option.value,
                            }))
                          }
                          className={`rounded-md border px-4 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ember)] ${
                            active
                              ? "border-[var(--color-ember)] bg-[var(--color-ember)] text-[var(--color-ember-foreground)]"
                              : "border-border hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
                          } ${optSoldOut ? "cursor-not-allowed opacity-40 line-through hover:border-border hover:text-inherit" : ""}`}
                        >
                          {optionLabel(option)}
                          {optSoldOut && (
                            <span className="sr-only"> — sold out</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              ))}
            </div>
          ) : null}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center rounded-md border border-border">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-3 py-2 text-lg hover:text-[var(--color-ember)]"
                aria-label="Decrease quantity"
              >
                −
              </button>
              <span className="w-10 text-center font-semibold">{qty}</span>
              <button
                onClick={() => setQty((q) => q + 1)}
                className="px-3 py-2 text-lg hover:text-[var(--color-ember)]"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
            <button
              disabled={soldOut}
              onClick={() => {
                if (soldOut) return;
                add(product.id, qty, selection);
                setAdded(true);
                setTimeout(() => setAdded(false), 2200);
              }}
              className="rounded-md bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {soldOut
                ? "Sold out"
                : added
                  ? `Added ${qty} to cart`
                  : "Add to cart"}
            </button>
            <Link
              to="/cart"
              className="rounded-md border border-border px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
            >
              View cart
            </Link>
          </div>

          {selectionLabel && (
            <p className="mt-3 text-xs text-muted-foreground">
              Adding: {selectionLabel}
            </p>
          )}

          <div className="mt-8 rounded-lg border border-border bg-card p-4 text-sm">
            <p className="font-semibold">Ships to your door — lower 48 states.</p>
            <p className="mt-1 text-muted-foreground">{SHIPPING_NOTE}</p>
          </div>
        </div>
      </div>

      <section className="border-t border-border bg-card/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <h2 className="font-display text-2xl">More from the lodge shop</h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.id}
                to="/shop/$productId"
                params={{ productId: p.id }}
                className="group rounded-xl border border-border bg-card overflow-hidden hover:border-[var(--color-ember)] transition-colors"
              >
                <div className="aspect-[4/3] bg-gradient-to-br from-[var(--color-navy)] to-[var(--color-navy-deep)] flex items-center justify-center overflow-hidden">
                  {p.image && (
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="p-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--color-ember)]">
                    {p.category}
                  </p>
                  <p className="mt-1 font-display text-lg group-hover:text-[var(--color-ember)] transition-colors">
                    {p.name}
                  </p>
                  <p className="mt-1 text-sm font-semibold">${p.price}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
